// Branded and custom wooden advent calendars. Targets (OpenSEO US, Nov 2026):
// wooden advent calendar 6,600 (Dec 33,100), custom advent calendar 1,600
// (+328% YoY), personalized advent calendar, branded advent calendar 260,
// corporate advent calendar 70, refillable wooden advent calendar. Angle:
// laser-cut wood or acrylic calendars with doors or drawers, a logo or names
// engraved or UV printed, made in New York in small and medium runs instead
// of an overseas factory minimum. The owner hasn't set a minimum or lead time
// and assembly time is unconfirmed, so both are "quoted per project"; the
// client fills them, or filling is quoted per project.
const {
  quoted,
  setup,
  each,
  small,
  costAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'adventCalendars',
  slug: 'advent-calendars',
  path: '/services/laser-engraving/advent-calendars/',
  noun: 'advent calendar',
  crumb: 'Advent calendars',
  reviewedDate: '2026-11-05',
  reviewedDateLabel: 'November 5, 2026',
  title: 'Wooden Advent Calendars, Custom & Branded | PrintAWorld',
  description:
    'Custom wooden advent calendars made in NYC: laser-cut wood or acrylic with doors or drawers, your logo or names engraved. Small runs, no factory minimum.',
  eyebrow: 'Advent calendars / laser cut in New York',
  heading:
    'Custom wooden advent calendars with your logo or names, laser cut in New York.',
  answer: `PrintAWorld makes custom wooden advent calendars in New York: laser-cut wood or acrylic calendars with 24 doors or drawers, with your logo, a pattern or family names engraved, or printed in color. Companies order branded advent calendars as client and employee gifts and for PR seeding, in small and medium runs instead of an overseas factory minimum. The calendars, filling, minimum and lead time are ${quoted.toLowerCase()}; engraving a logo or name is a ${setup} setup plus ${each(small, 'calendar')}.`,
  cta: 'Get a calendar quote',
  heroImage: 'logoDrawers',
  ideaImages: ['treeCalendar', 'familyName', 'corporateRun'],
  images: {
    logoDrawers: {
      file: 'logo-drawers',
      alt: 'Birch plywood advent calendar with 24 numbered drawers and a snowflake logo engraved on the top',
      caption: '24 drawers with your logo on top',
    },
    treeCalendar: {
      file: 'tree-calendar',
      alt: 'Flat plywood Christmas tree advent calendar on a wooden base with 24 small hinged doors, two open with wrapped treats',
      caption: 'A tree with 24 doors',
    },
    familyName: {
      file: 'family-names',
      alt: 'House-shaped walnut and maple advent calendar with 24 numbered drawers and a family name engraved on the roof',
      caption: 'A family name on the roof',
    },
    corporateRun: {
      file: 'corporate-run',
      alt: 'Six wooden advent boxes with the same hexagon logo engraved on the lid, some packed in kraft shipping boxes',
      caption: 'A branded run, packed to ship',
    },
  },
  intro: {
    eyebrow: 'Made to refill',
    title: 'A wooden advent calendar people bring out every December.',
    paragraphs: [
      'Cardboard calendars get recycled on December 25. A wooden one with your logo on it comes back out every year, refilled, so the gift keeps your name in front of the client.',
      'We laser cut the doors and drawers from wood or acrylic, engrave your logo or names, and print color where you want it. You fill them, or we quote the filling.',
    ],
  },
  ideas: {
    eyebrow: 'Calendar ideas',
    title: 'Drawer chests, tree calendars and family names.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title:
      'Branded advent calendars for clients, teams and brands, names for families.',
    items: [
      {
        label: 'Client gifts',
        title: 'A corporate advent calendar',
        body: 'Your logo on the lid and a small gift behind each door, sent to clients before December 1.',
      },
      {
        label: 'Employees',
        title: 'A calendar for every desk',
        body: 'The company logo plus each person’s name, from a spreadsheet you send.',
      },
      {
        label: 'Brands and PR',
        title: 'Seeding and influencer kits',
        body: 'Product samples behind each door, in a calendar shaped and colored like your brand.',
      },
      {
        label: 'Retailers',
        title: 'Calendars to sell',
        body: 'Your design in a run sized for your shop, priced per piece for each quantity.',
      },
      {
        label: 'Families',
        title: 'A personalized advent calendar',
        body: 'A family name or the children’s names engraved, to refill every year.',
      },
    ],
  },
  materials: {
    link: 'How they’re made',
    eyebrow: 'How they’re made',
    title: 'Laser-cut wood and acrylic, engraved or printed.',
    items: [
      {
        title: 'Laser-cut wood',
        body: 'Birch plywood or other wood, cut and engraved dark brown on our lasers.',
      },
      {
        title: 'Acrylic',
        body: 'Opaque acrylic in your brand colors for doors, fronts or a whole calendar.',
      },
      {
        title: 'Color printing',
        body: 'A full-color logo or artwork UV printed on the flat panels, with white ink on dark wood.',
      },
      {
        title: 'Doors or drawers',
        body: 'Hinged doors, sliding drawers or open cubbies, sized to what goes inside.',
      },
    ],
  },
  process: {
    title: 'From your logo to a box of calendars.',
    note: 'Lead time and minimums are quoted per project, since cutting and assembly depend on the design. For this December, ask now.',
    steps: [
      {
        title: 'Send the logo and count',
        body: 'Your logo or names, what goes inside each door, how many calendars and your in-hand date.',
      },
      {
        title: 'Approve the design',
        body: 'We send a drawing of the calendar with your artwork placed, then a sample before the run.',
      },
      {
        title: 'We cut and build',
        body: 'Cut, engraved or printed and assembled in our New York studio, each one checked.',
      },
      {
        title: 'Fill, pack and ship',
        body: 'Filled by you or by us (quoted per project), boxed and shipped or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Custom advent calendar pricing.',
    cta: 'Price my calendars',
    rows: [
      { label: 'Engraving a logo or name, per calendar', value: small },
      { label: 'The calendar: cutting and assembly', value: quoted },
      { label: 'Color by UV printing', value: quoted },
      { label: 'Filling and packing', value: quoted },
      { label: 'Minimum and lead time', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Branded advent calendars',
    title: 'A run sized to your client list, made in New York.',
    paragraphs: [
      'Overseas advent calendar factories usually want hundreds of pieces and months of lead time. We cut, engrave and assemble here, so the run can match the number of clients or staff you have. The minimum is quoted per project.',
      'We build and approve one calendar first, then the rest on the same setup, with names checked against your list.',
    ],
    cta: 'Plan a calendar order',
  },
  faqTitle: 'Advent calendar questions, answered.',
  faqs: [
    {
      question: 'How much does a custom wooden advent calendar cost?',
      answer: costAnswer(
        small,
        `The calendar itself (cutting and assembly), color printing, filling and packing are ${quoted.toLowerCase()}, since they depend on the size, the doors or drawers and the count.`
      ),
    },
    {
      question: 'Is there a minimum order?',
      answer: `The minimum is ${quoted.toLowerCase()}. Tell us how many you need; we build to your count instead of a factory minimum, and the ${setup} engraving setup is charged once per job, so a larger run costs less per calendar.`,
    },
    {
      question: 'How long does it take?',
      answer:
        'Lead time is quoted per project, because cutting and assembling a calendar takes longer than engraving one. Tell us your in-hand date first; calendars open on December 1. Rush jobs are possible case by case.',
    },
    {
      question: 'Do you fill the calendars?',
      answer: `You can fill them yourself, or we quote filling per project. Tell us the size of what goes inside so each door or drawer fits it.`,
    },
    {
      question: 'Are the calendars refillable?',
      answer:
        'Yes. The doors and drawers open and close, so the calendar can be refilled and used again every year.',
    },
    {
      question: 'Can each calendar have a different name?',
      answer:
        'Yes. Send the names in a spreadsheet. We keep the design the same and engrave a name on each calendar, checked against your list.',
    },
    {
      question: 'Can our logo be in color?',
      answer:
        'Yes. We UV print full-color artwork on the flat panels, with white ink for dark wood. Engraving alone gives a dark brown mark on wood.',
    },
    {
      question: 'Wood or acrylic?',
      answer:
        'Wood looks warm and traditional and engraves dark. Acrylic comes in brand colors. A calendar can mix them: a wood frame with colored acrylic doors.',
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your advent calendars.',
    body: 'Send the logo or names, what goes inside, how many and when you need them. We’ll reply with a design plan and a written quote.',
    checklist: [
      'Your logo (vector if you have it) or the names',
      'What goes in each door or drawer, and its size',
      'How many calendars, or several quantities to price',
      'Your in-hand date and delivery ZIP',
    ],
  },
  moreCards: ['christmasOrnaments', 'corporateGifts', 'prBoxes'],
  extraRelated: [
    ['Christmas ornaments', '/services/laser-engraving/christmas-ornaments/'],
    ['Corporate gifts', '/services/laser-engraving/corporate-gifts/'],
    ['Laser cutting', '/services/laser-cutting/'],
  ],
  schema: {
    name: 'Custom Wooden Advent Calendars',
    alternateName: [
      'Custom advent calendar',
      'Personalized advent calendar',
      'Branded advent calendar',
      'Corporate advent calendar',
      'Refillable wooden advent calendar',
    ],
    serviceType: 'Laser-cut and engraved advent calendars',
  },
})
